/**
 * ESS Hub — Employee Self-Service Dashboard
 * Route: /my-profile
 *
 * REDESIGN (this version) — visual refresh for a more attractive,
 * creative, and fully responsive experience across mobile, tablet,
 * and desktop. This is a STYLING/LAYOUT pass only — every data
 * binding, API call, and bug fix from previous versions is preserved
 * exactly as-is:
 *   - onStatusChange={setAttendance} passed to CheckInWidget (live
 *     refresh fix)
 *   - /leave/balance with days_used/total_available field mapping
 *   - attStatus derived from checked_in/checked_out/has_open_session
 *     (not the nonexistent `status` field)
 *   - uuid={user?.uuid} passed to EmployeeIdCard
 *
 * What changed visually:
 *   - Welcome banner: decorative layered shapes, glass-morphism
 *     buttons, stacks cleanly on mobile
 *   - Each major card now has a colored top accent + soft hover lift
 *   - Quick Actions tiles use richer gradient backgrounds, larger
 *     touch targets on mobile (2-column grid instead of cramped 3-4)
 *   - Leave dials get a subtle glow when balance is running low
 *   - Staggered fade-in entrance animation for cards (CSS keyframes
 *     injected once via a <style> tag — no new dependencies)
 *   - Explicit responsive breakpoints tuned beyond default Bootstrap
 *     stacking, with mobile-first touch target sizing (44px minimum)
 *
 * NEW DEPENDENCIES REQUIRED — none. Pure CSS/JSX changes.
 */
import React, { useState, useEffect } from 'react';
import {
    Container, Row, Col, Card, Badge, Button,
    ProgressBar, Spinner, Alert,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
    User, Calendar, Clock, CreditCard,
    BookOpen, FileText, CheckCircle, Bell,
    TrendingUp, Home, HelpCircle, ArrowRight,
    MapPin, Briefcase, Download, Award, IdCard,
    Sparkles, Sun, Moon, Sunset,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosConfig';
import EmployeeIdCard from '../../components/EmployeeIdCard';
import CheckInWidget from '../dashboard/CheckInWidget';

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-IN',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => !d ? '—' : new Date(d).toLocaleDateString('en-IN',
    { day:'numeric', month:'short', year:'numeric' });

const LETTER_TYPE_LABELS = {
    offer:           'Offer Letter',
    appointment:     'Appointment Letter',
    experience:      'Experience Certificate',
    salary_revision: 'Salary Revision Letter',
};

// ── One-time injected keyframes for entrance animation + small hover
// effects. Scoped via a unique class prefix so it can't collide with
// anything else in the app's global stylesheet. ─────────────────────────
const EssStyles = () => (
    <style>{`
        @keyframes essFadeUp {
            from { opacity: 0; transform: translateY(14px); }
            to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes essPulseGlow {
            0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.0); }
            50%      { box-shadow: 0 0 0 6px rgba(220,38,38,0.08); }
        }
        .ess-card-in {
            animation: essFadeUp 0.45s ease both;
        }
        .ess-hover-lift {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .ess-hover-lift:hover {
            transform: translateY(-3px);
            box-shadow: 0 12px 28px rgba(15,23,42,0.10) !important;
        }
        .ess-quick-tile {
            transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
        }
        .ess-quick-tile:hover {
            transform: translateY(-2px) scale(1.02);
        }
        .ess-dial-low {
            animation: essPulseGlow 2.2s ease-in-out infinite;
            border-radius: 50%;
        }
        .ess-glass-btn {
            background: rgba(255,255,255,0.18) !important;
            border: 1px solid rgba(255,255,255,0.35) !important;
            color: #fff !important;
            backdrop-filter: blur(6px);
        }
        .ess-glass-btn:hover {
            background: rgba(255,255,255,0.3) !important;
        }
        @media (max-width: 575.98px) {
            .ess-banner-actions { width: 100%; }
            .ess-banner-actions .btn { flex: 1; }
        }
    `}</style>
);

// ── Leave balance dial ────────────────────────────────────────────────────────
const LeaveDial = ({ label, used, total, color, delay = 0 }) => {
    const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
    const remaining = Math.max(0, total - used);
    const isLow = total > 0 && remaining <= Math.max(1, Math.round(total * 0.15));
    return (
        <div className="text-center ess-card-in" style={{ animationDelay: `${delay}ms` }}>
            <div
                className={isLow ? 'ess-dial-low' : ''}
                style={{ position:'relative', width:76, height:76, margin:'0 auto 8px' }}>
                <svg viewBox="0 0 36 36" style={{ transform:'rotate(-90deg)', width:76, height:76 }}>
                    <circle cx="18" cy="18" r="15.915"
                        fill="none" stroke="#f1f5f9" strokeWidth="3"/>
                    <circle cx="18" cy="18" r="15.915"
                        fill="none" stroke={color} strokeWidth="3.4"
                        strokeDasharray={`${pct} ${100 - pct}`}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dasharray 0.6s ease' }}/>
                </svg>
                <div style={{
                    position:'absolute', inset:0,
                    display:'flex', flexDirection:'column',
                    alignItems:'center', justifyContent:'center',
                }}>
                    <span style={{ fontSize:19, fontWeight:800, color: isLow ? '#dc2626' : color }}>{remaining}</span>
                    <span style={{ fontSize:8, color:'#94a3b8' }}>left</span>
                </div>
            </div>
            <div style={{ fontSize:11, fontWeight:700, color:'#374151' }}>{label}</div>
            <div style={{ fontSize:10, color:'#94a3b8' }}>{used}/{total} used</div>
        </div>
    );
};

// ── Quick action tile ────────────────────────────────────────────────────────
const QuickBtn = ({ icon: Icon, label, to, color = '#4f46e5' }) => (
    <Link to={to} style={{ textDecoration:'none' }}>
        <div
            className="ess-quick-tile"
            style={{
                display:'flex', flexDirection:'column', alignItems:'center',
                gap:8, padding:'18px 10px', borderRadius:16,
                background: `linear-gradient(150deg, ${color}10, ${color}06)`,
                border: `1px solid ${color}20`,
                cursor:'pointer', minHeight: 96,
                justifyContent: 'center',
            }}>
            <div style={{
                width:42, height:42, borderRadius:12,
                background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow: `0 4px 10px ${color}40`,
            }}>
                <Icon size={19} color="#fff"/>
            </div>
            <span style={{ fontSize:11.5, color:'#374151', fontWeight:700, textAlign:'center', lineHeight:1.25 }}>
                {label}
            </span>
        </div>
    </Link>
);

// ── Section heading helper — consistent icon-chip + title across cards ────
const SectionHeading = ({ icon: Icon, color, children, action }) => (
    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
            <span style={{
                width: 30, height: 30, borderRadius: 9,
                background: `${color}15`, display:'flex',
                alignItems:'center', justifyContent:'center', flexShrink:0,
            }}>
                <Icon size={15} color={color}/>
            </span>
            {children}
        </h6>
        {action}
    </div>
);

// ── Themed stat card with colored top accent ─────────────────────────────
const AccentCard = ({ color = '#4f46e5', delay = 0, className = '', children, style = {} }) => (
    <Card
        className={`border-0 shadow-sm mb-4 ess-card-in ess-hover-lift ${className}`}
        style={{
            borderRadius: 18,
            borderTop: `3px solid ${color}`,
            animationDelay: `${delay}ms`,
            overflow: 'hidden',
            ...style,
        }}>
        {children}
    </Card>
);
// ════════════════════════════════════════════════════════════════════════════
const ESSHub = () => {
    const { user } = useAuth();
    const [profile,    setProfile]    = useState(null);
    const [leave,      setLeave]      = useState([]);
    const [attendance, setAttendance] = useState(null);
    const [payslip,    setPayslip]    = useState(null);
    const [holidays,   setHolidays]   = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [letters,    setLetters]    = useState([]);
    const [downloadingLetterId, setDownloadingLetterId] = useState(null);
    const [showIdCard, setShowIdCard] = useState(false);
    const [loading,    setLoading]    = useState(true);

    // ── Data fetching — UNCHANGED from the working, bug-fixed version ────
    useEffect(() => {
        const today = new Date().toISOString().slice(0,10);
        Promise.allSettled([
            axiosInstance.get('/users/profile'),
            axiosInstance.get('/leave/balance'),
            axiosInstance.get('/attendance/today'),
            axiosInstance.get('/payroll/my-payslips').then(r =>
                (r.data.data || [])[0] || null),
            axiosInstance.get('/public/holidays/upcoming'),
            axiosInstance.get('/announcements'),
            axiosInstance.get('/letters/my'),
        ]).then(([prof, lv, att, pay, hols, ann, lets]) => {
            if (prof.status === 'fulfilled') setProfile(prof.value.data.data);
            if (lv.status   === 'fulfilled') setLeave(lv.value.data.data || []);
            if (att.status  === 'fulfilled') setAttendance(att.value.data.data);
            if (pay.status  === 'fulfilled') setPayslip(pay.value);
            if (hols.status === 'fulfilled') setHolidays((hols.value.data.data || []).slice(0,5));
            if (ann.status  === 'fulfilled') setAnnouncements((ann.value.data.data || []).slice(0,3));
            if (lets.status === 'fulfilled') setLetters(lets.value.data.data || []);
        }).finally(() => setLoading(false));
    }, []);

    const downloadLetter = async (letterId, letterType) => {
        setDownloadingLetterId(letterId);
        try {
            const res = await axiosInstance.get(`/letters/${letterId}/download`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a   = document.createElement('a');
            a.href     = url;
            a.download = `${(LETTER_TYPE_LABELS[letterType] || 'Letter').replace(/\s+/g, '_')}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Failed to download letter. Please try again.');
        } finally {
            setDownloadingLetterId(null);
        }
    };

    if (loading) return (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight:400 }}>
            <Spinner animation="border" variant="primary"/>
        </div>
    );

    const p = profile || user || {};
    const initials = [p.first_name?.[0], p.last_name?.[0]].filter(Boolean).join('').toUpperCase() || 'ME';
    const todayDate = new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long' });
    const checkedIn = attendance?.check_in;
    const checkedOut = attendance?.check_out;

    // BUG FIX (preserved): derived from fields that actually exist on
    // the API response — checked_in/checked_out/has_open_session —
    // NOT a nonexistent `status` field.
    const attStatus = attendance?.has_open_session ? 'Checked In'
        : attendance?.checked_out ? 'Present'
        : attendance?.checked_in  ? 'Present'
        : 'Not Marked';

    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
    const GreetingIcon = hour < 12 ? Sun : hour < 17 ? Sparkles : hour < 20 ? Sunset : Moon;

    return (
        <Container fluid className="p-3 p-md-4"> {/*  style={{ maxWidth: 1400 }} */}
            <EssStyles/>

            {/* ── Welcome banner — redesigned with layered decorative
                  shapes + glass-morphism action buttons, stacks cleanly
                  on mobile ─────────────────────────────────────────── */}
            <div
                className="ess-card-in"
                style={{
                    position: 'relative',
                    background: 'linear-gradient(135deg, var(--primary-color, #4f46e5) 0%, #7c3aed 55%, #a855f7 100%)',
                    borderRadius: 22,
                    padding: '28px 28px',
                    marginBottom: 24,
                    overflow: 'hidden',
                    boxShadow: '0 16px 40px rgba(79,70,229,0.28)',
                }}>
                {/* Decorative layered circles — purely CSS, no images */}
                <div style={{
                    position:'absolute', top:-60, right:-40, width:220, height:220,
                    borderRadius:'50%', background:'rgba(255,255,255,0.08)', pointerEvents:'none',
                }}/>
                <div style={{
                    position:'absolute', bottom:-70, right:90, width:140, height:140,
                    borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none',
                }}/>
                <div style={{
                    position:'absolute', top:10, left:'38%', width:60, height:60,
                    borderRadius:'50%', background:'rgba(255,255,255,0.05)', pointerEvents:'none',
                    display: 'none',
                }}/>

                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3"
                    style={{ position:'relative', zIndex:1 }}>
                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                        {p.avatar ? (
                            <img src={p.avatar} alt="avatar"
                                style={{ width:64, height:64, borderRadius:'50%',
                                    objectFit:'cover', border:'3px solid rgba(255,255,255,0.5)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.18)', flexShrink: 0 }}/>
                        ) : (
                            <div style={{
                                width:64, height:64, borderRadius:'50%',
                                background:'rgba(255,255,255,0.22)',
                                display:'flex', alignItems:'center', justifyContent:'center',
                                fontSize:23, fontWeight:800, color:'#fff',
                                border:'3px solid rgba(255,255,255,0.5)',
                                boxShadow: '0 6px 16px rgba(0,0,0,0.18)', flexShrink: 0,
                            }}>
                                {initials}
                            </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                            <h5 className="mb-0 text-white fw-bold d-flex align-items-center gap-2" style={{ flexWrap: 'wrap' }}>
                                <GreetingIcon size={18} style={{ flexShrink: 0 }}/>
                                <span>{greeting}, {p.first_name || 'there'}!</span>
                            </h5>
                            <div style={{ color:'rgba(255,255,255,0.82)', fontSize:13, marginTop:4 }}>
                                {p.designation_name || p.role_name || 'Employee'} ·{' '}
                                {p.department_name || 'PeoplesDock'}
                            </div>
                            <div style={{ color:'rgba(255,255,255,0.6)', fontSize:11.5, marginTop:1 }}>
                                {todayDate}
                            </div>
                        </div>
                    </div>
                    <div className="d-flex gap-2 ess-banner-actions">
                        <Button size="sm" className="ess-glass-btn rounded-pill px-3 d-flex align-items-center gap-1 fw-semibold"
                            style={{ fontSize:12 }}
                            onClick={() => setShowIdCard(true)}>
                            <IdCard size={13}/> My ID Card
                        </Button>
                        <Link to="/profile" style={{ flex: '0 0 auto' }} className="ess-banner-actions">
                            <Button size="sm" className="ess-glass-btn rounded-pill px-3 fw-semibold w-100"
                                style={{ fontSize:12 }}>
                                Edit Profile
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
            <Row className="g-4">
                {/* ── Left column ─────────────────────────────────────── */}
                <Col lg={8}>
                    {/* Check-in widget — UNCHANGED component, BUG FIX
                        preserved: onStatusChange={setAttendance} keeps
                        the Attendance card below live-updating without
                        a page refresh. */}
                    <div className="ess-card-in" style={{ animationDelay: '40ms' }}>
                        <CheckInWidget compact={false} onStatusChange={setAttendance} />
                    </div>

                    {/* Attendance card */}
                    <AccentCard color="#0891b2" delay={80}>
                        <Card.Body className="p-4">
                            <SectionHeading icon={Clock} color="#0891b2"
                                action={
                                    <Badge bg={
                                        attStatus === 'Checked In' ? 'warning' :
                                        attStatus === 'Present'    ? 'success' :
                                        'secondary'}
                                        style={{ fontSize:10.5, padding:'5px 10px', borderRadius: 20 }}>
                                        {attStatus}
                                    </Badge>
                                }>
                                Today's Attendance
                            </SectionHeading>
                            <Row className="g-2 g-sm-3">
                                {[
                                    { label:'Check In',  value: attendance?.check_in  || '—', color:'#059669', icon: '↓' },
                                    { label:'Check Out', value: attendance?.check_out || '—', color:'#dc2626', icon: '↑' },
                                    { label:'Work Hours',value: attendance?.work_hours
                                        ? `${attendance.work_hours}h` : '—', color:'#4f46e5', icon: '⏱' },
                                ].map(({ label, value, color }, i) => (
                                    <Col xs={4} key={i}>
                                        <div style={{
                                            background:`linear-gradient(150deg, ${color}10, ${color}05)`,
                                            border: `1px solid ${color}18`,
                                            borderRadius:14,
                                            padding:'14px 10px', textAlign:'center',
                                        }}>
                                            <div style={{ fontSize: window.innerWidth < 380 ? 15 : 19, fontWeight:800, color }}>
                                                {value}
                                            </div>
                                            <div style={{ fontSize:10.5, color:'#94a3b8', marginTop:3, fontWeight: 600 }}>
                                                {label}
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                            {!checkedIn && (
                                <Alert variant="info" className="small py-2 mt-3 mb-0" style={{ borderRadius: 12 }}>
                                    You haven't checked in today. Use the
                                    <Link to="/attendance" className="ms-1 fw-semibold">Attendance page</Link>.
                                </Alert>
                            )}
                        </Card.Body>
                    </AccentCard>

                    {/* Leave balances */}
                    <AccentCard color="#7c3aed" delay={120}>
                        <Card.Body className="p-4">
                            <SectionHeading icon={BookOpen} color="#7c3aed"
                                action={
                                    <Link to="/leave" style={{ fontSize:12, textDecoration:'none', color:'#4f46e5', fontWeight: 700 }}>
                                        Apply Leave →
                                    </Link>
                                }>
                                Leave Balances
                            </SectionHeading>
                            {leave.length === 0 ? (
                                <p className="text-muted small mb-0 text-center py-3">
                                    Leave balances not configured yet.
                                </p>
                            ) : (
                                <div className="d-flex gap-3 flex-wrap justify-content-around py-2">
                                    {leave.slice(0,5).map((lb, i) => (
                                        <LeaveDial key={i}
                                            label={lb.name || lb.leave_type_name}
                                            used={parseFloat(lb.days_used || 0)}
                                            total={parseFloat(lb.total_available ?? lb.max_days ?? 0)}
                                            color={['#4f46e5','#059669','#f59e0b','#0891b2','#dc2626'][i % 5]}
                                            delay={140 + i * 40}
                                        />
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </AccentCard>

                    {/* Latest payslip */}
                    {payslip && (
                        <AccentCard color="#0891b2" delay={160}>
                            <Card.Body className="p-4">
                                <SectionHeading icon={CreditCard} color="#0891b2"
                                    action={
                                        <Link to="/payroll/my-payslips"
                                            style={{ fontSize:12, textDecoration:'none', color:'#4f46e5', fontWeight: 700 }}>
                                            All payslips →
                                        </Link>
                                    }>
                                    Latest Payslip — {payslip.month}/{payslip.year}
                                </SectionHeading>
                                <Row className="g-2 g-sm-3">
                                    {[
                                        { label:'Gross Salary',     value:`₹${fmt(payslip.gross_salary)}`, color:'#0891b2' },
                                        { label:'Total Deductions', value:`₹${fmt(payslip.total_deductions || (parseFloat(payslip.pf_deduction||0)+parseFloat(payslip.tds_deduction||0)+parseFloat(payslip.pt_deduction||0)))}`, color:'#dc2626' },
                                        { label:'Net Take-Home',    value:`₹${fmt(payslip.net_salary)}`,   color:'#059669' },
                                    ].map(({ label, value, color }, i) => (
                                        <Col xs={4} key={i}>
                                            <div style={{
                                                background:`linear-gradient(150deg, ${color}10, ${color}05)`,
                                                border: `1px solid ${color}18`,
                                                borderRadius:14, padding:'14px 10px', textAlign:'center',
                                            }}>
                                                <div style={{ fontSize: window.innerWidth < 380 ? 13 : 15.5, fontWeight:800, color }}>{value}</div>
                                                <div style={{ fontSize:10.5, color:'#94a3b8', marginTop:3, fontWeight: 600 }}>{label}</div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </AccentCard>
                    )}

                    {/* Quick actions — richer gradient tiles, 2-col on
                        mobile for bigger touch targets, 4-col on desktop */}
                    <AccentCard color="#f59e0b" delay={200} className="mb-4">
                        <Card.Body className="p-4">
                            <SectionHeading icon={ArrowRight} color="#f59e0b">
                                Quick Actions
                            </SectionHeading>
                            <Row className="g-2 g-sm-3">
                                {[
                                    { icon:Calendar,   label:'Apply Leave',        to:'/leave',                 color:'#4f46e5' },
                                    { icon:Clock,      label:'My Attendance',       to:'/attendance',            color:'#0891b2' },
                                    { icon:FileText,   label:'My Documents',        to:'/documents',             color:'#059669' },
                                    { icon:CreditCard, label:'My Payslips',         to:'/payroll/my-payslips',   color:'#f59e0b' },
                                    { icon:TrendingUp, label:'My Performance',      to:'/performance',           color:'#7c3aed' },
                                    { icon:HelpCircle, label:'Raise Ticket',        to:'/helpdesk',              color:'#dc2626' },
                                    { icon:Home,       label:'WFH Request',         to:'/remote-work',           color:'#0891b2' },
                                    { icon:Clock,      label:'Regularize Attendance',to:'/attendance/regularize',color:'#f59e0b' },
                                ].map((action, i) => (
                                    <Col xs={6} sm={4} md={3} key={i}>
                                        <QuickBtn {...action}/>
                                    </Col>
                                ))}
                            </Row>
                        </Card.Body>
                    </AccentCard>
                </Col>
                {/* ── Right column ─────────────────────────────────────── */}
                <Col lg={4}>
                    {/* Profile summary */}
                    <AccentCard color="#4f46e5" delay={60}>
                        <Card.Body className="p-4">
                            <SectionHeading icon={User} color="#4f46e5">My Info</SectionHeading>
                            {[
                                { icon:Briefcase, label:'Employee Code', value: p.employee_code || '—' },
                                { icon:Calendar,  label:'Join Date',     value: fmtDate(p.join_date) },
                                { icon:MapPin,    label:'Location',      value: p.location_name || '—' },
                                { icon:User,      label:'Manager',       value: p.manager_name  || '—' },
                            ].map(({ icon: Icon, label, value }, i) => (
                                <div key={i} className="d-flex gap-3 mb-3 align-items-center">
                                    <div style={{
                                        width:34, height:34, borderRadius:10,
                                        background:'#eef2ff', flexShrink:0,
                                        display:'flex', alignItems:'center', justifyContent:'center',
                                    }}>
                                        <Icon size={15} color="#4f46e5"/>
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize:10, color:'#94a3b8', fontWeight: 600 }}>{label}</div>
                                        <div style={{ fontSize:13.5, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis' }}>{value}</div>
                                    </div>
                                </div>
                            ))}
                            <Button
                                variant="outline-primary" size="sm"
                                className="w-100 d-flex align-items-center justify-content-center gap-2 mt-2"
                                style={{ borderRadius: 12, minHeight: 44, fontWeight: 600 }}
                                onClick={() => setShowIdCard(true)}>
                                <IdCard size={14}/> View My ID Card
                            </Button>
                        </Card.Body>
                    </AccentCard>

                    {/* Upcoming holidays */}
                    <AccentCard color="#f59e0b" delay={100}>
                        <Card.Body className="p-4">
                            <SectionHeading icon={Calendar} color="#f59e0b">Upcoming Holidays</SectionHeading>
                            {holidays.length === 0 ? (
                                <p className="text-muted small mb-0 text-center">No upcoming holidays.</p>
                            ) : holidays.map((h, i) => (
                                <div key={i} className="d-flex gap-3 mb-3 align-items-center">
                                    <div style={{
                                        width:44, height:44, borderRadius:12,
                                        background:'linear-gradient(150deg,#fef3c7,#fde68a)',
                                        flexShrink:0,
                                        display:'flex', flexDirection:'column',
                                        alignItems:'center', justifyContent:'center',
                                        boxShadow: '0 2px 6px rgba(245,158,11,0.18)',
                                    }}>
                                        <span style={{ fontSize:15, fontWeight:800, color:'#92400e', lineHeight:1 }}>
                                            {new Date(h.date).getDate()}
                                        </span>
                                        <span style={{ fontSize:8.5, color:'#b45309', fontWeight: 600 }}>
                                            {new Date(h.date).toLocaleString('en-IN', { month:'short' })}
                                        </span>
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize:13, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis' }}>{h.name}</div>
                                        <div style={{ fontSize:10.5, color:'#94a3b8' }}>
                                            {new Date(h.date).toLocaleDateString('en-IN', { weekday:'long' })}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Card.Body>
                    </AccentCard>

                    {/* My Letters */}
                    <AccentCard color="#7c3aed" delay={140}>
                        <Card.Body className="p-4">
                            <SectionHeading icon={Award} color="#7c3aed">My Letters</SectionHeading>
                            {letters.length === 0 ? (
                                <p className="text-muted small mb-0 text-center">
                                    No letters have been generated for you yet.
                                </p>
                            ) : letters.map((l) => (
                                <div key={l.id}
                                    className="d-flex justify-content-between align-items-center gap-2 mb-3">
                                    <div className="d-flex gap-3 align-items-center" style={{ minWidth: 0 }}>
                                        <div style={{
                                            width:38, height:38, borderRadius:11,
                                            background:'linear-gradient(150deg,#ede9fe,#ddd6fe)',
                                            flexShrink:0,
                                            display:'flex', alignItems:'center', justifyContent:'center',
                                        }}>
                                            <FileText size={16} color="#7c3aed"/>
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontSize:12, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis' }}>
                                                {LETTER_TYPE_LABELS[l.letter_type] || l.letter_type}
                                            </div>
                                            <div style={{ fontSize:10, color:'#94a3b8' }}>
                                                {fmtDate(l.generated_at)}
                                            </div>
                                        </div>
                                    </div>
                                    <Button
                                        size="sm" variant="outline-primary"
                                        className="py-1 px-2 flex-shrink-0"
                                        style={{ borderRadius: 10, minWidth: 38, minHeight: 38 }}
                                        disabled={downloadingLetterId === l.id}
                                        onClick={() => downloadLetter(l.id, l.letter_type)}
                                        title="Download PDF">
                                        {downloadingLetterId === l.id
                                            ? <Spinner size="sm"/>
                                            : <Download size={13}/>}
                                    </Button>
                                </div>
                            ))}
                        </Card.Body>
                    </AccentCard>

                    {/* Announcements */}
                    <AccentCard color="#4f46e5" delay={180} className="mb-4">
                        <Card.Body className="p-4">
                            <SectionHeading icon={Bell} color="#4f46e5">Announcements</SectionHeading>
                            {announcements.length === 0 ? (
                                <p className="text-muted small mb-0 text-center">No announcements.</p>
                            ) : announcements.map((a, i) => (
                                <div key={i} style={{
                                    padding:'12px 14px', borderRadius:12,
                                    background:'linear-gradient(150deg,#f8fafc,#f1f5f9)',
                                    marginBottom:8,
                                    borderLeft:'3px solid #4f46e5',
                                }}>
                                    <div style={{ fontSize:12.5, fontWeight:700, marginBottom:3 }}>
                                        {a.title}
                                    </div>
                                    <div style={{ fontSize:11.5, color:'#64748b', lineHeight: 1.5 }}>
                                        {(a.content || a.message || '').slice(0, 80)}…
                                    </div>
                                </div>
                            ))}
                        </Card.Body>
                    </AccentCard>
                </Col>
            </Row>

            {/* My ID Card modal — UNCHANGED, uuid fix preserved */}
            <EmployeeIdCard
                show={showIdCard}
                onClose={() => setShowIdCard(false)}
                profile={p}
                uuid={user?.uuid}
                companyName={user?.company_name}
                logoUrl={user?.logo_url}
                primaryColor={user?.primary_color}
            />
        </Container>
    );
};
export default ESSHub;
